import { useState } from 'react';
import WebsiteSubTab from './performance/WebsiteSubTab';
import RankingsSubTab from './performance/RankingsSubTab';
import GbpSubTab from './performance/GbpSubTab';
import SchemaSubTab from './performance/SchemaSubTab';
import ListingsTab from './ListingsTab';
import { useAuth } from '../hooks/useAuth';
import type { Franchisee } from '../types';

type Props = { franchisee: Franchisee | null };

type SubTabId = 'perf-website' | 'perf-rankings' | 'perf-gmb' | 'perf-listings' | 'perf-schema';

const SUBS: ReadonlyArray<{ id: SubTabId; label: string; adminOnly?: boolean }> = [
  { id: 'perf-website', label: 'Website' },
  { id: 'perf-rankings', label: 'Rankings' },
  { id: 'perf-gmb', label: 'Google Business Profile' },
  { id: 'perf-listings', label: 'Directory Listings' },
  // Internal SEO tooling — franchisees and FBCs never see this. The API is
  // gated on the server too (requireRole('admin')); this only hides the entry.
  { id: 'perf-schema', label: 'Schema', adminOnly: true },
];

export default function PerformanceTab({ franchisee }: Props) {
  const [sub, setSub] = useState<SubTabId>('perf-website');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const subs = SUBS.filter((s) => !s.adminOnly || isAdmin);

  if (!franchisee) {
    return (
      <div className="tab-placeholder">
        <h3>Metrics</h3>
        <p>Select a franchisee to view their performance metrics.</p>
      </div>
    );
  }

  return (
    <>
      <div className="perf-sub-tabs">
        {subs.map((s, i) => (
          <span key={s.id} style={{ display: 'contents' }}>
            {/* Divider marking the start of the admin-only tools, so the
                boundary between franchisee-facing and internal tabs reads at a
                glance. Only shown when a franchisee-facing tab precedes it. */}
            {s.adminOnly && i > 0 && !subs[i - 1].adminOnly && (
              <span className="perf-sub-divider" aria-hidden="true" />
            )}
            <button
              className={`perf-sub-btn${sub === s.id ? ' active' : ''}${
                s.adminOnly ? ' admin-only' : ''
              }`}
              onClick={() => setSub(s.id)}
            >
              {s.label}
            </button>
          </span>
        ))}
      </div>

      {sub === 'perf-website' && <WebsiteSubTab af={franchisee.af_number} />}
      {sub === 'perf-rankings' && <RankingsSubTab franchisee={franchisee} />}
      {sub === 'perf-gmb' && <GbpSubTab af={franchisee.af_number} />}
      {sub === 'perf-listings' && <ListingsTab af={franchisee.af_number} />}
      {sub === 'perf-schema' && isAdmin && <SchemaSubTab af={franchisee.af_number} />}
    </>
  );
}
